/**
 * Parameters: what a widget measures, chosen by type or by name.
 *
 * A channel label is "<device> - <KEY>" ("LC-03 - LOAD_PEAK"). That gives every
 * channel a device and a parameter key; its unit gives it a physical quantity
 * ("Force (N)", "Acceleration (mg)"). A widget stores a *selection* over those:
 *
 * - by rule   - quantity, and optionally keys and devices. New devices of the
 *               same type appear on the dashboard without anyone editing it.
 * - by pick   - exact channel codes, for "these three gauges, in this order".
 */

import type { Channel } from "@/api/types";

export type ParamKind = "series" | "event" | "waveform";

export const KIND_LABEL: Record<ParamKind, string> = {
  series: "Time series",
  event: "Per event",
  waveform: "Waveform",
};

export interface ParamInfo {
  channel: Channel;
  device: string | null;
  deviceLabel: string;
  key: string;
  quantity: string;
  unit: string;
  kind: ParamKind;
}

export interface ParamSelection {
  channels?: string[];
  quantity?: string | null;
  unit?: string | null;
  keys?: string[];
  devices?: string[];
  pattern?: string | null;
  kinds?: ParamKind[];
}

export function kindOf(c: Channel): ParamKind {
  if (c.classification === "PRIMARY") return "waveform";
  if (c.classification === "SECONDARY") return "series";
  return "event";
}

export function paramInfo(c: Channel): ParamInfo {
  const cut = c.label.lastIndexOf(" - ");
  return {
    channel: c,
    device: c.device,
    // The name people gave the device wins over the wire label baked into the
    // channel label, which cannot follow a rename.
    deviceLabel: c.device_name || (cut > 0 ? c.label.slice(0, cut) : "Project"),
    key: cut > 0 ? c.label.slice(cut + 3) : c.label,
    quantity: c.units?.name ?? "Unitless",
    unit: c.units?.unit ?? "",
    kind: kindOf(c),
  };
}

/*
 * A channel's label as people should read it.
 *
 * Channel labels are "LC-03 - LOAD_1S": the device's wire label and the key.
 * The wire label is fixed - the MQTT topic and broker ACL are written against
 * it - so a device named "Span 14 north" keeps "LC-03" in every channel label.
 * This swaps in the display name for display, and leaves the label itself
 * alone for the selection patterns that match against it.
 */
export function channelName(c: Channel): string {
  if (!c.device_name) return c.label;
  const cut = c.label.lastIndexOf(" - ");
  return cut > 0 ? `${c.device_name} - ${c.label.slice(cut + 3)}` : c.label;
}

/** A device's name: its display name when set, else its wire label. */
export function deviceName(d: { label: string; display_name?: string | null }): string {
  return d.display_name?.trim() || d.label;
}

/** `*` wildcards only - the format existing display configs use. */
export function matchesPattern(label: string, pattern: string): boolean {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
  return new RegExp(`^${escaped}$`).test(label);
}

/** A selection with no criterion selects nothing, never "everything". */
export function hasCriteria(sel?: ParamSelection | null): boolean {
  return Boolean(
    sel &&
      (sel.channels?.length || sel.quantity || sel.unit || sel.keys?.length || sel.pattern),
  );
}

const natural = (a: Channel, b: Channel) =>
  a.label.localeCompare(b.label, undefined, { numeric: true });

export function resolveSelection(
  sel: ParamSelection | null | undefined,
  channels: Channel[],
  limit = 24,
): Channel[] {
  if (!sel || !hasCriteria(sel)) return [];

  if (sel.channels?.length) {
    const byCode = new Map(channels.map((c) => [c.code, c]));
    return sel.channels
      .map((code) => byCode.get(code))
      .filter((c): c is Channel => Boolean(c))
      .slice(0, limit);
  }

  const keys = sel.keys?.length ? new Set(sel.keys) : null;
  const devices = sel.devices?.length ? new Set(sel.devices) : null;
  const kinds = sel.kinds?.length ? new Set(sel.kinds) : null;
  return channels
    .filter((c) => {
      const p = paramInfo(c);
      if (p.kind === "waveform") return false;
      if (kinds && !kinds.has(p.kind)) return false;
      if (sel.quantity && p.quantity !== sel.quantity) return false;
      if (sel.unit && p.unit !== sel.unit) return false;
      if (keys && !keys.has(p.key)) return false;
      if (devices && (!c.device || !devices.has(c.device))) return false;
      if (sel.pattern && !matchesPattern(c.label, sel.pattern)) return false;
      return true;
    })
    .sort(natural)
    .slice(0, limit);
}

export interface QuantityGroup {
  quantity: string;
  unit: string;
  count: number;
}

export function quantitiesOf(channels: Channel[]): QuantityGroup[] {
  const groups = new Map<string, QuantityGroup>();
  for (const c of channels) {
    const p = paramInfo(c);
    if (p.kind === "waveform") continue;
    const id = `${p.quantity}|${p.unit}`;
    const g = groups.get(id) ?? { quantity: p.quantity, unit: p.unit, count: 0 };
    g.count += 1;
    groups.set(id, g);
  }
  return [...groups.values()].sort((a, b) => a.quantity.localeCompare(b.quantity));
}

export function keysOf(channels: Channel[]): string[] {
  return [...new Set(channels.map((c) => paramInfo(c).key))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
}

export function devicesOf(channels: Channel[]): { code: string; label: string }[] {
  const out = new Map<string, string>();
  for (const c of channels) if (c.device) out.set(c.device, paramInfo(c).deviceLabel);
  return [...out.entries()]
    .map(([code, label]) => ({ code, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

/** The candidates that exist on these channels, in candidate order. */
export function findKeys(channels: Channel[], candidates: string[]): string[] {
  const present = new Set(channels.filter((c) => kindOf(c) !== "waveform").map((c) => paramInfo(c).key));
  return candidates.filter((k) => present.has(k));
}

/** The first quantity whose name matches, e.g. /temp/i. */
export function findQuantity(channels: Channel[], pattern: RegExp): QuantityGroup | null {
  return quantitiesOf(channels).find((q) => pattern.test(q.quantity)) ?? null;
}

export function describeSelection(sel: ParamSelection | null | undefined, channels: Channel[]): string {
  if (!hasCriteria(sel)) return "No parameters chosen";
  const matched = resolveSelection(sel, channels, 1000);
  if (sel?.channels?.length) return `${matched.length} chosen parameter${matched.length === 1 ? "" : "s"}`;
  const parts = [
    sel?.quantity ? `${sel.quantity}${sel.unit ? ` (${sel.unit})` : ""}` : null,
    sel?.keys?.length ? sel.keys.join(", ") : null,
    sel?.devices?.length ? `${sel.devices.length} device${sel.devices.length === 1 ? "" : "s"}` : "all devices",
  ].filter(Boolean);
  return `${parts.join(" · ")} — ${matched.length} match${matched.length === 1 ? "" : "es"}`;
}
